import "@/App.css";
import "@/i18n";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { StoreConfigProvider } from "@/contexts/StoreConfigContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { CartProvider } from "@/contexts/CartContext";
import HomePage from "@/pages/storefront/HomePage";
import ShopPage from "@/pages/storefront/ShopPage";
import ProductDetailPage from "@/pages/storefront/ProductDetailPage";
import CartPage from "@/pages/storefront/CartPage";
import CheckoutPage from "@/pages/storefront/CheckoutPage";
import OrderConfirmationPage from "@/pages/storefront/OrderConfirmationPage";
import TrackOrderPage from "@/pages/storefront/TrackOrderPage";
import PolicyPage from "@/pages/storefront/PolicyPage";
import NotFoundPage from "@/pages/storefront/NotFoundPage";
import LoginPage from "@/pages/admin/LoginPage";
import AdminLayout from "@/components/admin/AdminLayout";
import DashboardPage from "@/pages/admin/DashboardPage";
import SettingsPage from "@/pages/admin/SettingsPage";
import ProductListPage from "@/pages/admin/ProductListPage";
import ProductFormPage from "@/pages/admin/ProductFormPage";
import BundleListPage from "@/pages/admin/BundleListPage";
import BundleFormPage from "@/pages/admin/BundleFormPage";
import ImportPage from "@/pages/admin/ImportPage";
import OrderListPage from "@/pages/admin/OrderListPage";
import OrderDetailPage from "@/pages/admin/OrderDetailPage";
import CouponListPage from "@/pages/admin/CouponListPage";
import UpsellListPage from "@/pages/admin/UpsellListPage";
import NotificationSettingsPage from "@/pages/admin/NotificationSettingsPage";
import AIUsagePage from "@/pages/admin/AIUsagePage";
import FeedCompliancePage from "@/pages/admin/FeedCompliancePage";
import SlideOutCart from "@/components/storefront/SlideOutCart";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <StoreConfigProvider>
          <AuthProvider>
            <CurrencyProvider>
              <CartProvider>
                <Routes>
                  {/* Storefront */}
                  <Route path="/" element={<HomePage />} />
                  <Route path="/shop" element={<ShopPage />} />
                  <Route path="/product/:handle" element={<ProductDetailPage />} />
                  <Route path="/cart" element={<CartPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
                  <Route path="/track" element={<TrackOrderPage />} />

                  {/* Policy Pages */}
                  <Route path="/privacy-policy" element={<PolicyPage />} />
                  <Route path="/terms-of-service" element={<PolicyPage />} />
                  <Route path="/shipping-policy" element={<PolicyPage />} />
                  <Route path="/returns" element={<PolicyPage />} />
                  <Route path="/contact" element={<PolicyPage />} />
                  <Route path="/page/:slug" element={<PolicyPage />} />

                  {/* Admin */}
                  <Route path="/admin/login" element={<LoginPage />} />
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<Navigate to="dashboard" replace />} />
                    <Route path="dashboard" element={<DashboardPage />} />
                    <Route path="products" element={<ProductListPage />} />
                    <Route path="products/new" element={<ProductFormPage />} />
                    <Route path="products/:handle/edit" element={<ProductFormPage />} />
                    <Route path="bundles" element={<BundleListPage />} />
                    <Route path="bundles/new" element={<BundleFormPage />} />
                    <Route path="bundles/:id/edit" element={<BundleFormPage />} />
                    <Route path="import" element={<ImportPage />} />
                    <Route path="orders" element={<OrderListPage />} />
                    <Route path="orders/:id" element={<OrderDetailPage />} />
                    <Route path="coupons" element={<CouponListPage />} />
                    <Route path="upsells" element={<UpsellListPage />} />
                    <Route path="notifications" element={<NotificationSettingsPage />} />
                    <Route path="ai" element={<AIUsagePage />} />
                    <Route path="feed" element={<FeedCompliancePage />} />
                    <Route path="settings" element={<SettingsPage />} />
                  </Route>

                  {/* 404 */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
                <SlideOutCart />
                <Toaster position="top-right" richColors theme="light" />
              </CartProvider>
            </CurrencyProvider>
          </AuthProvider>
        </StoreConfigProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
